import { useEmbedContext } from '@thoughtspot/blink-context';
import {
    embedErrorCodes,
    embedErrorSeverity,
    eventErrorType,
    EventType,
    isEmbeddedApp,
} from '@thoughtspot/embed-util';
import { translate, useTranslation } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { collectEvent, UserAction } from '@thoughtspot/metrics';
import { reportError } from '@thoughtspot/mfe-utilities';
import { trackException } from '@thoughtspot/observability';
import { MutedAlert } from '@thoughtspot/radiant-react/widgets/alert/muted/muted-alert';
import React, { useEffect } from 'react';
import { useNavigationType } from 'react-router-dom';
import {
    Action,
    getDisabledActionReason,
    getDisabledActions,
    getHiddenActions,
    getVisibleActions,
    shouldShowAction,
} from '/@utils/embed.util';
import { dispatchNonMemoEvent } from '/@utils/event-bridge/event-bridge';
import { Vertical } from '../layouts';
import { useRouteChanged } from '../use-route-changed';
import styles from './error-boundary.module.scss';

interface ErrorBoundaryState {
    hasError: boolean;
}
export interface ErrorBoundaryProp {
    hasRouter?: boolean;
    children?: any;
    // If ErrorBoundary is being used at a mfe root
    isRoot?: boolean;
}

const logger = create('error-boundary');
// TODO (@FE-Infra) - Make this a user action
const ERROR_PAGE_SHOWN = 'ERROR_PAGE_SHOWN';
const GENERIC_TS_ERROR = 'TSError';

export const FallbackComponent = () => {
    const { t } = useTranslation();
    const embedConfig = useEmbedContext();
    const showPrimaryAction = embedConfig.isEmbedded
        ? shouldShowAction(
              getVisibleActions(embedConfig),
              getHiddenActions(embedConfig),
              Action.ReportError,
          )
        : true;
    const disablePrimaryAction = getDisabledActions(embedConfig).has(
        Action.ReportError,
    );
    const disabledActionReason = getDisabledActionReason(
        embedConfig,
        Action.ReportError,
    );

    return (
        <Vertical
            className={styles.crashPage}
            hAlignContent="center"
            vAlignContent="center"
            data-testid="crash-page"
            grow
        >
            <MutedAlert
                global
                imageVisible
                title={t('answerInitError.title')}
                description={t('answerInitError.detail')}
                {...(showPrimaryAction && {
                    primaryAction: {
                        label: t('alertService.REPORT'),
                        onClick: reportError,
                        isDisabled: disablePrimaryAction,
                        ...(disablePrimaryAction && {
                            tooltip: disabledActionReason,
                        }),
                    },
                })}
                secondaryAction={{
                    label: t('answerInitError.retry'),
                    onClick: () => {
                        window.location.reload();
                    },
                }}
            />
        </Vertical>
    );
};

/**
 * ErrorBoundaryUtil uses useNavigationType to reset error state on navigation change.
 *
 * NOTE: Navigation hooks can only be used if error boundary is inside router.
 * If ErrorBoundary is used in those components which are used outside of react router and in V1
 * then set hasRouter value as per their mounting env
 */
export const ErrorBoundaryUtil = (props: any) => {
    const { hasRouter = false, hasError, children, resetError } = props;
    const navigationType = hasRouter && useNavigationType();
    const routeChanged = hasRouter && useRouteChanged();

    useEffect(() => {
        // When route or hash changes, call resetError to clear the error state
        if (
            hasRouter &&
            hasError &&
            navigationType &&
            routeChanged &&
            navigationType !== 'POP'
        ) {
            resetError();
        }
    }, [navigationType, routeChanged]);

    if (hasError) {
        return <FallbackComponent />;
    }

    return children;
};

// Have to use class component for this as there is no hook
// for componentDidCatch https://stackoverflow.com/a/59932120
// so for now doesn't seem possible to turn it into a functional component

export class ErrorBoundary extends React.Component<
    ErrorBoundaryProp,
    ErrorBoundaryState
> {
    private hasLoggedError = false;

    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: any) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidCatch(error: any, errorInfo: any) {
        if (this.hasLoggedError) {
            return;
        }
        let errorObject: any = null;

        try {
            errorObject = {
                errorMessage: error?.message
                    ? JSON.stringify(error.message)
                    : null,
                errorName: error?.name,
            };
        } catch (error) {
            errorObject = null;
        }

        const eventMetrics: UserAction = {
            type: ERROR_PAGE_SHOWN,
            name: ERROR_PAGE_SHOWN,
            tags: [],
            messageParams: null,
            shouldSendEventToExperimentationService: false,
        };
        collectEvent(eventMetrics, {
            errorObject,
        });
        trackException(
            error,
            {
                errorObject,
            },
            ERROR_PAGE_SHOWN,
        );
        logger.error(
            {
                message: error?.message,
                stack: error?.stack,
                name: GENERIC_TS_ERROR,
            },
            error,
            errorInfo,
        );

        if (this.props.isRoot && isEmbeddedApp()) {
            dispatchNonMemoEvent(EventType.Error, {
                errorType: eventErrorType.GENERIC_ERROR,
                code: embedErrorCodes.EMBED_CRASH,
                severity: embedErrorSeverity.SEV1,
                message: error?.message || translate('answerInitError.title'),
            });
        }

        this.hasLoggedError = true;
    }

    resetError = () => {
        this.setState({ hasError: false });
        this.hasLoggedError = false;
    };

    render() {
        return (
            <ErrorBoundaryUtil
                hasError={this.state.hasError}
                resetError={this.resetError}
                {...this.props}
            />
        );
    }
}
